import { createContext, useContext, useEffect, useState } from "react";

import { usePlatform } from "~/hooks/use-platform";

type Theme = "dark" | "light" | "system";
type FontSize = "small" | "medium" | "large";

interface Appearance {
  theme: Theme;
  fontSize: FontSize;
}

interface ThemeProviderProps {
  children: React.ReactNode;
  defaultAppearance?: Appearance;
  storageKey?: string;
}

interface ThemeProviderState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
  fontSize: FontSize;
  setFontSize: (fontSize: FontSize) => void;
}

const initialState: ThemeProviderState = {
  theme: "system",
  setTheme: () => null,
  fontSize: "medium",
  setFontSize: () => null,
};

const ThemeProviderContext = createContext<ThemeProviderState>(initialState);

export function ThemeProvider({
  children,
  defaultAppearance = { theme: "system", fontSize: "medium" },
  storageKey = "ui-appearance",
}: ThemeProviderProps) {
  const { platform } = usePlatform();

  // Get appearance from localStorage or fallback to default
  const [appearance, setAppearance] = useState<Appearance>(() => {
    const stored = localStorage.getItem(storageKey);
    try {
      return stored
        ? { ...defaultAppearance, ...JSON.parse(stored) }
        : defaultAppearance;
    } catch {
      return defaultAppearance; // Fallback in case of JSON parsing error
    }
  });

  // Sync appearance changes with localStorage
  useEffect(() => {
    localStorage.setItem(storageKey, JSON.stringify(appearance));
  }, [appearance, storageKey]);

  // Notify React Native WebView about theme changes
  useEffect(() => {
    if (platform === "IOS" || platform === "ANDROID") {
      window.ReactNativeWebView?.postMessage(
        JSON.stringify({
          event: "theme",
          data: { theme: appearance.theme },
        }),
      );
    }
  }, [platform, appearance.theme]);

  // Update root class list for theme and font size
  useEffect(() => {
    const root = document.documentElement;

    const classList = root.classList;
    classList.remove(
      "light",
      "dark",
      "undefined",
      "text-small",
      "text-medium",
      "text-large",
      "text-undefined",
    );

    classList.add(`text-${appearance.fontSize}`);
    if (appearance.theme === "system") {
      const systemTheme = window.matchMedia("(prefers-color-scheme: dark)")
        .matches
        ? "dark"
        : "light";
      classList.add(systemTheme);
    } else {
      classList.add(appearance.theme);
    }
  }, [appearance]);

  // Context value
  const value: ThemeProviderState = {
    theme: appearance.theme,
    fontSize: appearance.fontSize,
    setTheme: (theme: Theme) => setAppearance((prev) => ({ ...prev, theme })),
    setFontSize: (fontSize: FontSize) =>
      setAppearance((prev) => ({ ...prev, fontSize })),
  };

  return (
    <ThemeProviderContext.Provider value={value}>
      {children}
    </ThemeProviderContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeProviderContext);
  if (!context) {
    throw new Error("useTheme must be used within a ThemeProvider");
  }
  return context;
};
